import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import HeaderRouter from '../components/HeaderRouter';
import Quiz20 from '../components/quiz20/Quiz20';
import { 
  Trophy, 
  ShieldCheck, 
  HelpCircle, 
  ArrowLeft, 
  Sparkles, 
  Loader2, 
  AlertTriangle 
} from 'lucide-react';
import './Challenge20Page.css';
import {startChallenge20K} from '../api/studentApi';

export default function Challenge20Page() {
  const { topicSlug } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [topicData, setTopicData] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [gameStarted, setGameStarted] = useState(false);

  useEffect(() => {
    async function loadChallenge() {
      try {
        setLoading(true);
        setError(null);

        const res = await startChallenge20K();

        const data = await res.data;
        if (!data.questions || data.questions.length < 15) {
          throw new Error('This topic does not have enough questions to start the 20K Challenge.');
        }

        setTopicData(data.topic);
        setQuestions(data.questions);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }

    loadChallenge();
  }, [topicSlug]);

  const handleFinish = async (result) => {
    try {
      await fetch('/api/minigames/challenge20k/complete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          topicSlug: topicSlug || 'computer-keyboard-basics',
          finalScore: result.score,
          outcome: result.outcome, // 'win' | 'walked' | 'wrong'
          reachedQuestion: result.reachedQuestion,
        }),
      });
    } catch (err) {
      console.error('Failed to submit challenge score:', err);
    }
  };

  return (
    <div className="curriculum-page-wrapper">
      <HeaderRouter />

      <main className="challenge20-main-content">

        {loading && (
          <div className="challenge20-state-box">
            <Loader2 size={36} className="spin-icon" />
            <p>Setting up the 20K Hotseat...</p>
          </div>
        )}

        {!loading && error && (
          <div className="challenge20-state-box error-box">
            <AlertTriangle size={36} />
            <h3>Unable to Start Challenge</h3>
            <p>{error}</p>
            <button 
              type="button" 
              className="challenge20-btn-primary" 
              onClick={() => navigate(-1)}
            >
              Choose Another Topic
            </button>
          </div>
        )}

        {!loading && !error && !gameStarted && (
          <div className="challenge20-lobby-card">
            <div className="challenge20-trophy-badge">
              <Trophy size={42} />
            </div>

            <h1 className="challenge20-title">Knolup 20K Challenge</h1>
            <p className="challenge20-subtitle">
              Answer 15 progressive questions correctly to reach the summit and bank <strong>20,000 Points</strong>!
            </p>

            <div className="challenge20-rules-panel">
              <div className="rule-row">
                <Sparkles size={20} className="rule-icon gold" />
                <div>
                  <strong>Doubling Points</strong>
                  <span>Every right answer pushes your score higher up the ladder.</span>
                </div>
              </div>

              <div className="rule-row">
                <ShieldCheck size={20} className="rule-icon emerald" />
                <div>
                  <strong>Safety Milestones</strong>
                  <span>Lock in guaranteed points at Question 5 (80 pts) and Question 10 (2,500 pts).</span>
                </div>
              </div>

              <div className="rule-row">
                <HelpCircle size={20} className="rule-icon sky" />
                <div>
                  <strong>Walk Away Option</strong>
                  <span>Unsure about a tricky question? Bank your current points before you risk it.</span>
                </div>
              </div>
            </div>

            <button 
              type="button" 
              className="challenge20-btn-primary full-width"
              onClick={() => setGameStarted(true)}
            >
              Enter the Hotseat 🚀
            </button>
          </div>
        )}

        {!loading && !error && gameStarted && (
          <div className="challenge20-game-frame">
            <Quiz20 
              questions={questions} 
              onFinish={handleFinish} 
            />
          </div>
        )}
      </main>
    </div>
  );
}